import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    EditButton,
    TextInput,
    TopToolbar,
    ExportButton,
    RefreshButton,
    CreateButton,
    useTranslate,
    FunctionField,
    usePermissions,
    BooleanField,
    SelectInput,
    FilterButton,
    useRecordContext,
    useListContext,
    RecordContextProvider,
} from 'react-admin';
import { Box, Chip, Typography, Stack, Card, CardContent, CardActionArea, Fab, useMediaQuery, useTheme } from '@mui/material';
import {
    Groups as GroupsIcon,
    Email as EmailIcon,
    Sms as SmsIcon,
    WhatsApp as WhatsAppIcon,
    NotificationsActive as PushIcon,
    Chat as SlackIcon,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const channelIcons: Record<string, React.ReactElement> = {
    email: <EmailIcon sx={{ fontSize: 14 }} />,
    sms: <SmsIcon sx={{ fontSize: 14 }} />,
    whatsapp: <WhatsAppIcon sx={{ fontSize: 14 }} />,
    push: <PushIcon sx={{ fontSize: 14 }} />,
    slack: <SlackIcon sx={{ fontSize: 14 }} />,
};

const getChannelColor = (channel: string) => {
    const colors: Record<string, string> = {
        email: '#1976D2',
        sms: '#4CAF50',
        whatsapp: '#25D366',
        push: '#FF6B35',
        slack: '#4A154B',
    };
    return colors[channel] || '#757575';
};

const typeColors: Record<string, string> = {
    site: '#FF6B35',
    charger: '#009A75',
    payment: '#1976D2',
    system: '#9C27B0',
    custom: '#757575',
};

const wrappedChipSx = {
    maxWidth: '100%',
    minWidth: 0,
    '& .MuiChip-label': {
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        lineHeight: 1.2,
        py: 0.5,
    },
};

const GroupFilters = [
    <TextInput
        key="search"
        source="q"
        label="resources.notification-groups.list.filters.search"
        alwaysOn
        sx={{
            '& .MuiInputBase-root': {
                backgroundColor: 'white'
            }
        }}
    />,
    <SelectInput
        key="type"
        source="type"
        label="resources.notification-groups.list.filters.type"
        choices={[
            { id: 'site', name: 'Site' },
            { id: 'charger', name: 'Charger' },
            { id: 'payment', name: 'Payment' },
            { id: 'system', name: 'System' },
            { id: 'custom', name: 'Custom' },
        ]}
        sx={{
            '& .MuiInputBase-root': {
                backgroundColor: 'white'
            }
        }}
    />,
    <SelectInput
        key="active"
        source="active"
        label="resources.notification-groups.list.filters.active"
        choices={[
            { id: 'true', name: 'Active' },
            { id: 'false', name: 'Inactive' },
        ]}
        sx={{
            '& .MuiInputBase-root': {
                backgroundColor: 'white'
            }
        }}
    />,
];

const MobileGroupFilters = [
    <TextInput
        key="search"
        source="q"
        label="resources.notification-groups.list.filters.search"
        alwaysOn
        sx={{
            '& .MuiInputBase-root': {
                backgroundColor: 'white'
            }
        }}
    />,
];

const DesktopListActions = () => {
    const { permissions } = usePermissions();
    const canCreate = permissions?.includes('notifications:write:all');

    return (
        <TopToolbar>
            <FilterButton />
            {canCreate && <CreateButton />}
            <ExportButton />
            <RefreshButton />
        </TopToolbar>
    );
};

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, p: 0 }}>
        <RefreshButton />
        <ExportButton />
    </Box>
);

const TypeField = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record || !record.type) return null;

    return (
        <Chip
            label={translate(`resources.notification-groups.types.${record.type}`)}
            size="small"
            sx={{
                backgroundColor: typeColors[record.type] || '#757575',
                color: 'white',
                fontWeight: 500,
            }}
        />
    );
};

const ChannelsField = () => {
    const record = useRecordContext();

    if (!record || !record.channels || !Array.isArray(record.channels)) return null;

    return (
        <Stack direction="row" spacing={0.5}>
            {record.channels.map((channel: string) => (
                <Chip
                    key={channel}
                    icon={channelIcons[channel]}
                    label={channel}
                    size="small"
                    variant="outlined"
                    sx={{
                        borderColor: getChannelColor(channel),
                        color: getChannelColor(channel),
                        '& .MuiChip-icon': {
                            color: getChannelColor(channel),
                        },
                    }}
                />
            ))}
        </Stack>
    );
};

const MemberCountField = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record) return null;

    return (
        <Chip
            label={`${record.memberCount || 0} ${translate('resources.notification-groups.list.members')}`}
            size="small"
            variant="filled"
            sx={{
                backgroundColor: '#F5F5F5',
                color: '#606060',
            }}
        />
    );
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const translate = useTranslate();
    const navigate = useNavigate();

    if (isLoading || !data) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Card sx={{ borderRadius: 2 }}>
                        <CardActionArea onClick={() => navigate(`/notification-groups/${record.id}`)}>
                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flex: 1,
                                            mr: 1,
                                        }}
                                    >
                                        {record.name}
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: '50%',
                                            backgroundColor: record.active ? '#4CAF50' : '#BDBDBD',
                                            flexShrink: 0,
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                                    {record.type && (
                                        <Chip
                                            label={translate(`resources.notification-groups.types.${record.type}`)}
                                            size="small"
                                            sx={{
                                                ...wrappedChipSx,
                                                backgroundColor: typeColors[record.type] || '#757575',
                                                color: 'white',
                                                fontWeight: 500,
                                                height: 'auto',
                                            }}
                                        />
                                    )}
                                    {Array.isArray(record.channels) && record.channels.map((channel: string) => (
                                        <Chip
                                            key={channel}
                                            icon={channelIcons[channel]}
                                            label={channel}
                                            size="small"
                                            variant="outlined"
                                            sx={{
                                                ...wrappedChipSx,
                                                borderColor: getChannelColor(channel),
                                                color: getChannelColor(channel),
                                                height: 'auto',
                                                '& .MuiChip-icon': {
                                                    color: getChannelColor(channel),
                                                },
                                            }}
                                        />
                                    ))}
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ color: '#606060' }}>
                                        {record.memberCount || 0} {translate('resources.notification-groups.list.members')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#9E9E9E' }}>
                                        {record.updatedAt ? new Date(record.updatedAt).toLocaleDateString() : ''}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const GroupList = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    if (!permissions?.includes('notifications:read:all')) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>{translate('ra.page.not_found')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={isMobile ? MobileGroupFilters : GroupFilters}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                perPage={25}
                sort={{ field: 'name', order: 'ASC' }}
                empty={isMobile ? false : undefined}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                        '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                        '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                        '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <TextField
                            source="name"
                            label="resources.notification-groups.fields.name"
                        />
                        <TextField
                            source="description"
                            label="resources.notification-groups.fields.description"
                        />
                        <FunctionField
                            label="resources.notification-groups.fields.type"
                            render={() => <TypeField />}
                        />
                        <FunctionField
                            label="resources.notification-groups.fields.channels"
                            render={() => <ChannelsField />}
                        />
                        <FunctionField
                            label="resources.notification-groups.fields.memberCount"
                            render={() => <MemberCountField />}
                        />
                        <BooleanField
                            source="active"
                            label="resources.notification-groups.fields.active"
                        />
                        <DateField
                            source="updatedAt"
                            label="resources.notification-groups.fields.updatedAt"
                            showTime
                        />
                        <EditButton label="" />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab
                    color="primary"
                    onClick={() => navigate('/notification-groups/create')}
                    sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}
                >
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
